import React, { useMemo, useCallback, useImperativeHandle, type KeyboardEvent } from 'react';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useRadioSelectCustomField } from '@atlassian/jira-issue-field-radio-select/src/services/index.tsx';
import type { RadioOption } from '@atlassian/jira-issue-field-radio-select/src/ui/edit/types.tsx';
import {
	mapOptionToData,
	mapDataToOptions,
} from '@atlassian/jira-issue-field-radio-select/src/ui/edit/utils.tsx';
import type { Option } from '@atlassian/jira-issue-field-radio-select-editview-full/src/ui/radio-select/types.tsx';
import { RADIO_BUTTONS_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { CellWrapper } from '../../../common/cell-wrapper/index.tsx';
import type { TransformedRadioSelectField } from '../../../common/field-types/radio-select/types.tsx';
import type { BasicListViewItem, FieldCellProps } from '../../../common/types.tsx';
import { RadioSelectEdit } from './edit/async.tsx';
import messages from './messages.tsx';
import { RadioSelectRead } from './read/index.tsx';

const transformToOption = (option: RadioOption | null | undefined): Option | null => {
	if (!option) {
		return null;
	}
	const { id, value } = option;
	return { value: id, label: value };
};

const RadioSelectOld = ({
	rowData: issue,
	cellData: field,
	column: { width: preferredWidth },
	editMode,
	setEditMode,
}: FieldCellProps<BasicListViewItem, TransformedRadioSelectField>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleFailure = useCallback(
		(err: Error) => {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'field updateFailed',
				'radioSelectIssueField',
				{
					error: err,
				},
			);
		},
		[createAnalyticsEvent],
	);

	const [{ value: customFieldValue, options: radioOptions }, { saveValue }] =
		useRadioSelectCustomField({
			issueKey: issue.key,
			fieldKey: field?.fieldId || '',
			initialValue: field?.value ?? null,
			onFailure: handleFailure,
		});

	const option = transformToOption(customFieldValue);

	const emptyOption = useMemo(
		() => ({
			label: formatMessage(messages.noValue),
			value: '',
		}),
		[formatMessage],
	);

	/**
	 * Options supplied to the RadioSelect component. An empty option may be present to allow
	 * for deselection.
	 */
	const options = useMemo<Array<Option>>(() => {
		const result = mapDataToOptions(radioOptions);

		// TODO: When we get around to supporting isRequired, we need to omit the emptyOption
		if (option) {
			result.unshift(emptyOption);
		}

		return result;
	}, [option, emptyOption, radioOptions]);

	/**
	 * Given the new selected option, save and move back to read only mode.
	 */
	const handleChange = useCallback(
		(newValue: Option | null) => {
			const newOption = newValue?.value ? newValue : null;

			const transformForSave = mapOptionToData({
				value: newOption?.value ?? '',
				label: newOption?.label ?? '',
			});
			saveValue(transformForSave, null, createAnalyticsEvent({}));
			setEditMode(false);
		},
		[saveValue, createAnalyticsEvent, setEditMode],
	);

	const isEditable = useMemo(
		() => issue.fieldConfig?.isEditable || false,
		[issue.fieldConfig?.isEditable],
	);

	if (field == null) {
		return null;
	}

	const placeholder = formatMessage(messages.placeholder, {
		fieldName: field.name,
	});

	const label = formatMessage(messages.label, {
		fieldName: field.name,
	});

	return (
		<CellWrapper
			isEditable={isEditable}
			hasPadding={false}
			testId="business-fields.ui.radio-select.cell.cell-wrapper"
		>
			{editMode ? (
				<RadioSelectEdit
					fieldId={field.id}
					fieldKey={field.fieldId}
					fieldName={field.name}
					label={label}
					placeholder={placeholder}
					option={option}
					options={options}
					readContent={<RadioSelectRead>{option?.label ?? ''}</RadioSelectRead>}
					preferredWidth={preferredWidth}
					onChange={handleChange}
					setEditMode={setEditMode}
				/>
			) : (
				<RadioSelectRead>{option?.label ?? ''}</RadioSelectRead>
			)}
		</CellWrapper>
	);
};

const RadioSelectWithRelay = ({
	rowData: issue,
	cellData: field,
	column: { width: preferredWidth },
	editMode,
	cellRef,
	setEditMode,
	wrapperRef,
}: FieldCellProps<BasicListViewItem, TransformedRadioSelectField>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleFailure = useCallback(
		(err: Error) => {
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				'field updateFailed',
				'radioSelectIssueField',
				{
					error: err,
				},
			);
		},
		[createAnalyticsEvent],
	);

	const [{ value }, { saveValue }] = useRadioSelectCustomField({
		issueKey: issue.key,
		fieldKey: field?.fieldId || '',
		initialValue: field?.value ?? null,
		onFailure: handleFailure,
	});

	const option = transformToOption(value);

	/**
	 * Given the new selected option, save and move back to read only mode.
	 */
	const handleChange = useCallback(
		(newValue: Option | null) => {
			const transformForSave = newValue
				? mapOptionToData({ label: newValue.label, value: newValue.value })
				: null;

			if (transformForSave?.id !== option?.value) {
				saveValue(transformForSave, null, createAnalyticsEvent({}));
			}

			setEditMode(false);
		},
		[option, saveValue, createAnalyticsEvent, setEditMode],
	);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLElement>) => {
			if (event.key === 'Delete' || event.key === 'Backspace') {
				wrapperRef &&
					setTimeout(() => {
						wrapperRef?.current?.focus();
					});
			}

			return false;
		},
		[wrapperRef],
	);

	const cellOverrides = useCallback(
		() => ({
			onKeyDown: handleKeyDown,
		}),
		[handleKeyDown],
	);

	useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

	const isEditable = useMemo(
		() => issue.fieldConfig?.isEditable || false,
		[issue.fieldConfig?.isEditable],
	);

	if (field == null) {
		return null;
	}

	const label = formatMessage(messages.label, {
		fieldName: field.name,
	});

	return (
		<CellWrapper
			isEditable={isEditable}
			hasPadding={false}
			testId="business-fields.ui.radio-select.cell.cell-wrapper"
		>
			{editMode ? (
				<RadioSelectEdit
					fieldId={field.id}
					fieldKey={field.fieldId}
					fieldName={field.name}
					label={label}
					option={option}
					readContent={<RadioSelectRead>{option?.label ?? ''}</RadioSelectRead>}
					preferredWidth={preferredWidth}
					onChange={handleChange}
					setEditMode={setEditMode}
				/>
			) : (
				<RadioSelectRead>{option?.label ?? ''}</RadioSelectRead>
			)}
		</CellWrapper>
	);
};

export const RadioSelect = componentWithFG(
	'jira-list-view-radio-cell-relay-edit-view',
	RadioSelectWithRelay,
	RadioSelectOld,
);

export const radioSelectCellHandler = {
	[RADIO_BUTTONS_CF_TYPE]: RadioSelect,
};
